/**
 * @description A hook for retrieving the mapping definition list
 */

import React from 'react';

import { ToastContext } from '@seccon/core/contexts/toast';
import decodeJson from '@seccon/shared/utils/decodeJson';

/**
 * @returns {{ data: object[], error: boolean}} - the data array of mapping definitions, the error state, and a
 *  callback function that refetches the data
 */
const useMappingDefinitionList = () => {
  const toastApi = React.useContext(ToastContext);

  // Maintains the mapping defintions list state
  const [data, setData] = React.useState(null);

  // Maintains the error state of the fetch
  const [error, setError] = React.useState(false);

  /**
   * Handles fetch success by storing the response in the list state
   * @param {object | object[]} response - api response
   */
  function handleSuccess (response) {
    setData(response ?? []);
  }

  /**
   * Handles fetch failures by notifying the user
   */
  function handleFailure () {
    toastApi.error('Personnel sync mapping definition list could not be retrieved. Reload the page and try again.');
    setError(true);
  }

  /**
   * Fetches the mapping definitions and handles its success or failure response
   */
  function handleFetch () {
    fetch('/mappings')
      .then(decodeJson)
      .then(handleSuccess)
      .catch(handleFailure)
  }

  /**
   * On mount, fetches a list of mapping definitions
   */
  React.useEffect(() => {
    // If the historys state is already defined, exit
    if (data !== null) {
      return;
    }

    handleFetch();
  }, []);

  return {
    data,
    error,
    refresh: handleFetch
  };
};

export default useMappingDefinitionList;
